import { Calendar, DatePicker } from "@progress/kendo-react-dateinputs";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import {
  AccumulationChartComponent,
  AccumulationDataLabel,
  AccumulationLegend,
  AccumulationSeriesCollectionDirective,
  AccumulationSeriesDirective,
  BarSeries,
  Category,
  ChartComponent,
  ColumnSeries,
  DataLabel,
  DateTime,
  Inject,
  Legend,
  LineSeries,
  PieSeries,
  RangeColorSettingDirective,
  RangeColorSettingsDirective,
  Selection,
  SeriesCollectionDirective,
  SeriesDirective,
  Tooltip,
  Zoom,
} from "@syncfusion/ej2-react-charts";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppContext from "../../../AppContext";
import axios from "axios";
import { formatDate } from "@telerik/kendo-intl";
import { BASE_URL } from "../../../constant";
import { useTranslation } from "react-i18next";

const TravelAndFuelAnalytics = () => {
  const { t } = useTranslation();
  const weeks = [
    { weekNum: 0, weekName: "Monday" },
    { weekNum: 1, weekName: "Tuesday" },
    { weekNum: 2, weekName: "Wednesday" },
    { weekNum: 3, weekName: "Thursday" },
    { weekNum: 4, weekName: "Friday" },
    { weekNum: 5, weekName: "Saturday" },
    { weekNum: 6, weekName: "Sunday" },
  ];
  const navigate = useNavigate();
  const { assets, darkMode } = useContext(AppContext);
  const [filterState, setFilterState] = useState({
    date: new Date(),
    asset: "all",
  });
  const [assetData, setAssetData] = useState([]);
  // const [analyticData, setAnalyticData] = useState();
  const [analyzedData, setAnalyzedData] = useState();

  useEffect(() => {
    if (assets)
      setAssetData([
        { id: "all", name: "ALL" },
        ...assets.map((x) => ({
          id: x.id,
          name: x.name,
        })),
      ]);
  }, [assets]);

  useEffect(() => {
    handleApplyFilter();
  }, []);

  const handleApplyFilter = () => {
    const { asset, date } = filterState;
    const assetName =
      asset !== "all" ? assetData.find((x) => x.id === asset).name : asset;
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const apiUrl = `${BASE_URL}/api/AnalyticsData?name=${encodeURIComponent(
      assetName
    )}&year=${year}&month=${month}`;

    // Make the API call using Axios
    axios
      .get(apiUrl)
      .then((response) => {
        // Handle the API response here
        // const kilometerConsumptionPerLiterList = response.data
        //   .map((entry) => {
        //     const { kmsTravelled, totalFuelConsumedLtrs } = entry;
        //     return (kmsTravelled || 0) / (totalFuelConsumedLtrs || 1);
        //   })
        //   .filter((x) => !isNaN(x));
        const validDailyMileages = response.data.filter(
          (data) => data.kmsTravelled > 0 && data.totalFuelConsumedLtrs > 0
        );
        const totalMileage = validDailyMileages.reduce(
          (acc, data) => acc + data.kmsTravelled / data.totalFuelConsumedLtrs,
          0
        );
        const data = {
          kmsTravelled: response.data.reduce((c, v) => c + v.kmsTravelled, 0),
          avgKm:
            response.data.reduce((c, v) => c + v.kmsTravelled, 0) /
            response.data.length,
          avgFuelConsumption:
            response.data.reduce((c, v) => c + v.totalFuelConsumedLtrs, 0) /
            response.data.length,
          totalRefual: response.data.reduce((c, v) => c + v.totalRefuelLtrs, 0),
          kmPerLtr: (totalMileage / validDailyMileages.length) * 2.8,
          // kilometerConsumptionPerLiterList.reduce(
          //   (acc, val) => acc + val,
          //   0
          // ) / kilometerConsumptionPerLiterList.length,
          kmPerDaily: response.data.map((x) => ({
            dateTime: new Date(x.local_date),
            km: x.kmsTravelled,
          })),
          FuelConsumptionPerDaily: response.data.map((x) => ({
            dateTime: new Date(x.local_date),
            fuel: x.totalFuelConsumedLtrs,
          })),
          ReFuelPerDaily: response.data.map((x) => ({
            dateTime: new Date(x.local_date),
            fuel: x.totalRefuelLtrs,
          })),
          weekTravel: weeks
            .map((x) => ({
              week: x.weekName,
              kmsTravelled: response.data
                .filter((y) => y.weekDay === x.weekNum)
                .reduce((acc, y) => acc + y.kmsTravelled, 0),
            }))
            .filter((z) => z.kmsTravelled !== 0),
          usageOnHolidays: response.data
            .filter((x) => x.weekDay === 6)
            .map((x) => ({
              dateTime: new Date(x.local_date),
              refuel: x.totalRefuelLtrs,
              km: x.kmsTravelled,
            })),
        };
        setAnalyzedData(data);
        // setAnalyticData(response.data); // You can update your state or perform other actions based on the response
      })
      .catch((error) => {
        // Handle errors
        console.error("Error fetching data:", error);
        // You can set an error state or display an error message to the user
      });
  };

  const GetCalendar = (props) => {
    return <Calendar {...props} bottomView="year" topView="decade" />;
  };

  const LabelTemplate = (props) => {
    return (
      <div className="flex flex-col items-end font-sans">
        <label>{props.point.x}</label>
        <label>{props.point.y}</label>
      </div>
    );
  };

  const color1 = ["#F9D422"];
  const color2 = ["#F28F3F"];
  const color3 = ["#E94F53"];
  const color4 = ["#FFFFFF"];

  const textRender = (args) => {
    if (args.text === "0") {
      args.cancel = args.point.y === 0;
    }
  };
  return (
    <div className="flex flex-col dark:text-neutral-200 text-labels px-5 py-3 pb-5 gap-5 dark:bg-darkModeHomeBg bg-lightModeBg">
      <div className="font-medium dark:text-white text-labels flex items-center justify-between">
        <div className="flex text-xl items-center gap-2">
          <button type="button" onClick={() => navigate("/analytics")}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="2.0"
              stroke="currentColor"
              className="w-5 h-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19.5 12h-15m0 0l6.75 6.75M4.5 12l6.75-6.75"
              />
            </svg>
          </button>
          {t("masters.headerTexts.travelAndFuel")}
        </div>
        <div className="flex gap-2">
          <div id="compo" className={`${darkMode ? "darkCombo" : "lightCombo"}`}>
            <ComboBox
              id="default-search"
              textField="name"
              data={assetData}
              dataItemKey="id"
              name="asset"
              style={{
                fontSize: "12px",
                fontWeight: "500",
                borderColor: "transparent",
              }}
              clearButton={false}
              className=" block border-gray-400 dark:text-white text-inputFieldsTxts rounded-lg mb-4 h-8"
              placeholder="Select Asset"
              onChange={(event) =>
                setFilterState((prevState) => ({
                  ...prevState,
                  asset: event.target.value.id,
                }))
              }
              value={assetData.find((x) => x.id === filterState.asset) || null}
              required
            />
          </div>
          <div className={`${darkMode ? "darkCombo" : "lightCombo"}`}>
            <DatePicker
              format="MMMM-yyy"
              style={{
                fontSize: "12px",
                fontWeight: "500",
                borderColor: "transparent",
              }}
              className=" block border-gray-400 dark:text-white text-inputFieldsTxts rounded-lg mb-4 h-8"
              value={filterState.date}
              onChange={(e) =>
                setFilterState((pre) => ({ ...pre, date: e.value }))
              }
              calendar={(props) => GetCalendar(props)}
            />
          </div>
          <button
            type="button"
            className="dark:bg-indigo-600 bg-applyBtn md:px-2 text-xs px-1 font-medium md:h-7 h-5 my-auto rounded"
            onClick={() => handleApplyFilter()}
          >
            {t("button.apply")}
          </button>
        </div>
      </div>
      <div className="flex dark:bg-neutral-800 bg-TFACards rounded-md">
        <div className="flex flex-col w-1/5 gap-1 items-center py-3">
          <label className="text-4xl dark:text-white text-labels font-medium">
            {analyzedData && analyzedData.kmsTravelled
              ? parseFloat(analyzedData.kmsTravelled.toFixed(2))
              : 0}{" "}
            <label className="text-base">km</label>
          </label>
          <label className="dark:text-neutral-400 text-labels">
            {t("masters.labels.ttlKmTraveled")}
          </label>
        </div>
        <div className="flex flex-col w-1/5 gap-1 items-center py-3">
          <label className="text-4xl dark:text-white text-labels font-medium">
            {analyzedData && analyzedData.avgKm
              ? parseFloat(analyzedData.avgKm.toFixed(2))
              : 0}{" "}
            <label className="text-base">km</label>
          </label>
          <label className="dark:text-neutral-400 text-labels">
            {t("masters.labels.avgKmPerDay")}
          </label>
        </div>
        <div className="flex flex-col w-1/5 gap-1 items-center py-3">
          <label className="text-4xl dark:text-white text-labels font-medium">
            {analyzedData && analyzedData.kmPerLtr
              ? parseFloat(analyzedData.kmPerLtr.toFixed(2))
              : 0}{" "}
            <label className="text-base">km/L</label>
          </label>
          <label className="dark:text-neutral-400 text-labels">
            {t("masters.labels.kmPerLtr")}
          </label>
        </div>
        <div className="flex flex-col w-1/5 gap-1 items-center py-3">
          <label className="text-4xl dark:text-white text-labels font-medium">
            {analyzedData && analyzedData.avgFuelConsumption
              ? parseFloat(analyzedData.avgFuelConsumption.toFixed(2))
              : 0}{" "}
            <label className="text-base">L</label>
          </label>
          <label className="dark:text-neutral-400 text-labels">
            {t("masters.labels.avgFuelPerDay")}
          </label>
        </div>
        <div className="flex flex-col w-1/5 gap-1 items-center py-3">
          <label className="text-4xl dark:text-white text-labels font-medium">
            {analyzedData && analyzedData.totalRefual
              ? parseFloat(analyzedData.totalRefual.toFixed(2))
              : 0}{" "}
            <label className="text-base">L</label>
          </label>
          <label className="dark:text-neutral-400 text-labels">
            {t("masters.labels.ttlRefuelPerPeriod")}
          </label>
        </div>
      </div>
      <div className="w-full h-72 flex gap-5">
        <div className="h-full w-3/4 dark:bg-neutral-800 bg-TFACards rounded-md">
          <ChartComponent
            id="charts444"
            primaryXAxis={{
              valueType: "DateTime",
              majorGridLines: { width: 0 },
              minorGridLines: { width: 0 },
              labelFormat: "dd-MMM",
              labelIntersectAction: "Hide",
              labelStyle: { size: "10px", color: "white" },
            }}
            primaryYAxis={{
              majorGridLines: { width: 0 },
              valueType: "Double",
              minorGridLines: { width: 0 },
              labelStyle: { size: "10px", color: "white" },
            }}
            textRender={textRender}
            tooltip={{ enable: true }}
            className="bg-transparent"
            zoomSettings={{
              enablePan: true,
              enableSelectionZooming: true,
              mode: "X",
            }}
          >
            <Inject
              services={[
                ColumnSeries,
                Zoom,
                LineSeries,
                DateTime,
                Legend,
                Tooltip,
                DataLabel,
                Category,
              ]}
            />
            <SeriesCollectionDirective>
              <SeriesDirective
                dataSource={analyzedData?.kmPerDaily || []}
                xName="dateTime"
                yName="km"
                name={t("masters.labels.km")}
                type="Column"
                marker={{
                  dataLabel: {
                    visible: true,
                    font: { color: "rgb(255,255,255)" },
                    format: "{value}",
                  },
                }}
              ></SeriesDirective>
              <SeriesDirective
                dataSource={analyzedData?.FuelConsumptionPerDaily || []}
                xName="dateTime"
                yName="fuel"
                name={t("masters.labels.fuelConsume")}
                type="Line"
                fill="yellow"
                width={2}
                marker={{
                  dataLabel: {
                    visible: true,
                    font: { color: "rgb(255,255,255)" },
                    format: "{value}",
                  },
                }}
              ></SeriesDirective>
            </SeriesCollectionDirective>
          </ChartComponent>
        </div>
        <div className="px-5 py-1 w-1/4 dark:bg-neutral-800 bg-TFACards rounded-md">
          <label className="text-lg dark:text-white text-labels font-medium">
            {t("masters.labels.usageOnUnScheduledTime")}
          </label>
          <div className="flex flex-col gap-2 overflow-y-auto mt-2">
            {analyzedData
              ? analyzedData.usageOnHolidays.map((x, i) => (
                  <div
                    key={i}
                    className="border-l-2 px-3 py-1 font-medium border-neutral-200"
                  >
                    <label className="text-blue-500">
                      {t("masters.labels.sunday")} ,{" "}
                      {formatDate(new Date(x.dateTime), "MMM-dd yyyy")}
                    </label>
                    <div className="flex text-sm w-full justify-between">
                      <div className=" flex flex-col">
                        <label className="dark:text-white text-labels font-medium">
                          {x.refuel}
                        </label>
                        <label className="dark:text-neutral-400 text-labels font-medium">
                          {t("masters.labels.refuel")}(ltrs)
                        </label>
                      </div>
                      <div className=" flex flex-col">
                        <label className="dark:text-white text-labels font-medium">{x.km}</label>
                        <label className="dark:text-neutral-400 text-labels font-medium">
                          {t("masters.labels.km")}
                        </label>
                      </div>
                    </div>
                  </div>
                ))
              : null}
          </div>
        </div>
      </div>
      <div className="flex h-3/5 gap-5">
        <div className="w-1/2 h-full dark:bg-neutral-800 bg-TFACards dark:text-white text-labels rounded-md p-4">
          <label className="text-lg dark:text-white text-labels font-medium">
            {t("masters.labels.refuel")}
          </label>
          <ChartComponent
            id="charts"
            primaryXAxis={{
              valueType: "DateTime",
              majorGridLines: { width: 0 },
              minorGridLines: { width: 0 },
              labelFormat: "dd-MMM",
              labelIntersectAction: "Hide",
              labelStyle: { size: "10px", color: "white" },
            }}
            primaryYAxis={{
              majorGridLines: { width: 0 },
              minorGridLines: { width: 0 },
              labelStyle: { size: "10px", color: "white" },
            }}
            textRender={textRender}
            tooltip={{ enable: true }}
            className="bg-transparent h-max"
            legendSettings={{ visible: true, position: "Top" }}
            selectionMode="Point"
            zoomSettings={{
              enablePan: true,
              enableSelectionZooming: true,
              mode: "X",
            }}
          >
            <Inject
              services={[
                BarSeries,
                DateTime,
                Legend,
                Tooltip,
                DataLabel,
                Category,
                Selection,
              ]}
            />
            <SeriesCollectionDirective>
              <SeriesDirective
                dataSource={analyzedData?.ReFuelPerDaily || []}
                xName="dateTime"
                yName="fuel"
                type="Bar"
                marker={{
                  dataLabel: {
                    visible: true,
                    font: { color: "rgb(255,255,255)" },
                    format: "{value}",
                  },
                }}
              ></SeriesDirective>
            </SeriesCollectionDirective>
            <RangeColorSettingsDirective>
              <RangeColorSettingDirective
                label=""
                start={0}
                end={100}
                colors={color4}
              ></RangeColorSettingDirective>
              <RangeColorSettingDirective
                label=""
                start={100}
                end={200}
                colors={color1}
              ></RangeColorSettingDirective>
              <RangeColorSettingDirective
                label=""
                start={200}
                end={300}
                colors={color2}
              ></RangeColorSettingDirective>
              <RangeColorSettingDirective
                label=""
                start={300}
                end={400}
                colors={color3}
              ></RangeColorSettingDirective>
            </RangeColorSettingsDirective>
          </ChartComponent>
        </div>
        <div className="w-1/2 flex gap-3 flex-col h-full dark:bg-neutral-800 bg-TFACards rounded-md p-4">
          <div className="h-3/4 px-5">
            <label className="text-lg dark:text-white text-labels font-medium">
              {t("masters.labels.WeekDayTravelAnalysis")}
            </label>
            <AccumulationChartComponent
              id="chartssss"
              legendSettings={{ visible: true }}
              textRender={textRender}
            >
              <Inject
                services={[
                  PieSeries,
                  AccumulationLegend,
                  AccumulationDataLabel,
                ]}
              />
              <AccumulationSeriesCollectionDirective>
                <AccumulationSeriesDirective
                  dataSource={analyzedData?.weekTravel || []}
                  xName="week"
                  yName="kmsTravelled"
                  dataLabel={{
                    visible: true,
                    name: "week",
                    position: "Outside",
                    font: { color: "white" },
                    template: LabelTemplate,
                  }}
                  type="Pie"
                />
              </AccumulationSeriesCollectionDirective>
            </AccumulationChartComponent>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TravelAndFuelAnalytics;
